(function () {
  "use strict";

  if (window.MarineMaxCoveoFilterHydration) return;

  const FACET_SELECTION_SEPARATOR = "\u001E";
  const MAX_STALLED_ATTEMPTS = 4;
  const REQUIRED_FUNCTION_DEPENDENCIES = Object.freeze([
    "facetIdsMatchCondition",
    "facetIdsMatchMake",
    "facetIdsMatchModel",
    "getCoveoCanonicalFacetIdForFriendlyUrlKey",
    "getCoveoUrlStateLookupKeys",
    "isInternalCoveoUrlFacet",
    "normalizeCoveoUrlFacetId",
    "setTimeout",
    "warn",
  ]);

  function createCoveoFilterHydration(dependencies) {
    const deps = dependencies || {};
    const missingDependencies = REQUIRED_FUNCTION_DEPENDENCIES.filter(
      function (name) {
        return typeof deps[name] !== "function";
      },
    );
    if (
      !deps.statePort ||
      typeof deps.statePort.read !== "function" ||
      typeof deps.statePort.write !== "function"
    ) {
      missingDependencies.push("statePort.read/write");
    }
    if (missingDependencies.length) {
      throw new Error(
        `[Coveo Filter Hydration] Missing dependencies: ${missingDependencies.join(", ")}`,
      );
    }

    const {
      facetIdsMatchCondition,
      facetIdsMatchMake,
      facetIdsMatchModel,
      getCoveoCanonicalFacetIdForFriendlyUrlKey,
      getCoveoUrlStateLookupKeys,
      isInternalCoveoUrlFacet,
      normalizeCoveoUrlFacetId,
      setTimeout,
      statePort,
      warn,
    } = deps;

    function readState(key) {
      return statePort.read(key);
    }

    function writeState(key, value) {
      statePort.write(key, value);
      return value;
    }

    function preparePendingInitialState(state, defaultConditionValues) {
      if (!(state?.facetValues instanceof Map)) return state;
      const hasExplicitConditionSelection = Array.from(
        state.facetValues.keys(),
      ).some(function (facetKey) {
        return facetIdsMatchCondition(facetKey);
      });
      if (hasExplicitConditionSelection) return state;

      state.facetValues.set(
        "condition",
        Array.isArray(defaultConditionValues)
          ? defaultConditionValues.slice()
          : [],
      );
      state.defaultConditionSelectionApplied = true;
      return state;
    }

    function primePendingState(state) {
      writeState("primaryRequestPatched", false);
      return writeState(
        "pendingState",
        state && typeof state === "object" ? state : null,
      );
    }

    function getPendingState() {
      const state = readState("pendingState");
      return state && typeof state === "object" ? state : null;
    }

    function resolvePendingFacetValues(facetId, stateSnapshot) {
      const state =
        arguments.length > 1 ? stateSnapshot : getPendingState();
      if (!(state?.facetValues instanceof Map)) return [];
      const lookupKeys = getCoveoUrlStateLookupKeys(facetId);
      for (let i = 0; i < lookupKeys.length; i++) {
        const found = state.facetValues.get(lookupKeys[i]);
        if (Array.isArray(found) && found.length) return found;
      }
      return [];
    }

    function resolvePendingFacetRange(facetId, stateSnapshot) {
      const state =
        arguments.length > 1 ? stateSnapshot : getPendingState();
      if (!(state?.facetRanges instanceof Map)) return null;
      const lookupKeys = getCoveoUrlStateLookupKeys(facetId);
      for (let i = 0; i < lookupKeys.length; i++) {
        const found = state.facetRanges.get(lookupKeys[i]);
        if (found) return found;
      }
      return null;
    }

    function getPendingRegularFacetEntries(stateSnapshot) {
      const state = arguments.length ? stateSnapshot : getPendingState();
      if (!(state?.facetValues instanceof Map)) return [];
      const entriesByFacetId = new Map();
      state.facetValues.forEach(function (values, rawKey) {
        const key = normalizeCoveoUrlFacetId(rawKey);
        const facetId =
          getCoveoCanonicalFacetIdForFriendlyUrlKey(key) || key;
        if (
          !facetId ||
          isInternalCoveoUrlFacet(facetId) ||
          !Array.isArray(values) ||
          !values.length
        ) {
          return;
        }
        const normalizedValues = Array.from(
          new Set(
            values
              .map(function (value) {
                return String(value || "").trim();
              })
              .filter(Boolean),
          ),
        );
        if (!normalizedValues.length) return;
        entriesByFacetId.set(facetId, {
          facetId,
          values: normalizedValues,
        });
      });
      return Array.from(entriesByFacetId.values());
    }

    function facetValueSetsEqual(currentValues, expectedValues) {
      const normalizeValues = function (values) {
        return Array.isArray(values)
          ? Array.from(
              new Set(
                values
                  .map(function (value) {
                    return String(value || "").trim();
                  })
                  .filter(Boolean),
              ),
            ).sort()
          : [];
      };
      const current = normalizeValues(currentValues);
      const expected = normalizeValues(expectedValues);
      if (current.length !== expected.length) return false;
      for (let i = 0; i < current.length; i++) {
        if (current[i] !== expected[i]) return false;
      }
      return true;
    }

    function facetRangesEqual(currentRange, expectedRange) {
      const currentLo = Number(currentRange?.lo);
      const currentHi = Number(currentRange?.hi);
      const expectedLo = Number(expectedRange?.lo);
      const expectedHi = Number(expectedRange?.hi);
      if (
        !Number.isFinite(currentLo) ||
        !Number.isFinite(currentHi) ||
        !Number.isFinite(expectedLo) ||
        !Number.isFinite(expectedHi)
      ) {
        return false;
      }
      return (
        Math.abs(currentLo - expectedLo) < 1e-6 &&
        Math.abs(currentHi - expectedHi) < 1e-6
      );
    }

    function getFacetPriority(facetId) {
      if (facetIdsMatchMake(facetId)) return 0;
      if (facetIdsMatchModel(facetId)) return 2;
      return 1;
    }

    function buildDesiredFacetSelectionSnapshot(
      currentSnapshot,
      facetId,
      pendingValues,
    ) {
      const desiredSnapshot =
        currentSnapshot instanceof Map
          ? new Map(currentSnapshot)
          : new Map();
      const normalizedFacetId = String(facetId || "").trim();
      if (!normalizedFacetId) return desiredSnapshot;

      desiredSnapshot.forEach(function (_checked, key) {
        const separatorIndex = key.indexOf(FACET_SELECTION_SEPARATOR);
        if (
          separatorIndex === -1 ||
          key.slice(0, separatorIndex) !== normalizedFacetId
        ) {
          return;
        }
        desiredSnapshot.set(key, false);
      });
      (Array.isArray(pendingValues) ? pendingValues : []).forEach(
        function (value) {
          desiredSnapshot.set(
            `${normalizedFacetId}${FACET_SELECTION_SEPARATOR}${String(value || "")}`,
            true,
          );
        },
      );
      return desiredSnapshot;
    }

    function beginHydrationLease() {
      const pendingState = getPendingState();
      if (!pendingState) return null;
      const revision = Number(readState("revision") || 0);
      return Object.freeze({
        pendingState,
        revision,
        isCurrent() {
          return (
            Number(readState("revision") || 0) === revision &&
            getPendingState() === pendingState
          );
        },
      });
    }

    function retirePendingHydration() {
      const inFlightHydration = readState("promise");
      writeState("revision", Number(readState("revision") || 0) + 1);
      writeState("pendingState", null);
      writeState("meta", { attempts: 0 });
      return inFlightHydration &&
        typeof inFlightHydration.then === "function"
        ? inFlightHydration
        : null;
    }

    function waitForRetiredHydration(hydrationPromise, timeoutMs) {
      if (!hydrationPromise || typeof hydrationPromise.then !== "function") {
        return Promise.resolve();
      }
      const maxWait = Math.max(250, Number(timeoutMs) || 3000);
      return Promise.race([
        Promise.resolve(hydrationPromise).catch(function () {}),
        new Promise(function (resolve) {
          setTimeout(resolve, maxWait);
        }),
      ]);
    }

    function finishHydration(lease, outcome) {
      const result = String(outcome || "");
      if (result === "cancelled") return "cancelled";
      if (lease && !lease.isCurrent()) return "cancelled";

      if (result === "matched" || result === "empty") {
        writeState("pendingState", null);
        writeState("meta", { attempts: 0 });
        return result;
      }

      if (result === "applied" || result === "stalled") {
        const currentMeta = readState("meta");
        const attempts =
          Number(
            currentMeta && typeof currentMeta === "object"
              ? currentMeta.attempts
              : 0,
          ) + 1;
        writeState("meta", { attempts });
        if (result === "stalled" && attempts >= MAX_STALLED_ATTEMPTS) {
          warn(
            "[Coveo URL] Shared-link filters could not be fully restored from URL params.",
          );
          writeState("pendingState", null);
          writeState("meta", { attempts: 0 });
        }
      }
      return result;
    }

    function scheduleHydration(reconcile) {
      if (!getPendingState() || typeof reconcile !== "function") return null;
      if (readState("inFlight")) return null;

      writeState("inFlight", true);
      let hydrationPromise = null;
      hydrationPromise = Promise.resolve()
        .then(function () {
          const lease = beginHydrationLease();
          if (!lease) return { lease: null, outcome: "empty" };
          return Promise.resolve(reconcile(lease)).then(function (outcome) {
            return { lease, outcome };
          });
        })
        .then(function (settled) {
          return finishHydration(settled?.lease || null, settled?.outcome);
        })
        .catch(function (error) {
          warn("[Coveo URL] Pending shared-link hydration failed:", error);
        })
        .finally(function () {
          writeState("inFlight", false);
          if (readState("promise") === hydrationPromise) {
            writeState("promise", null);
          }
        });
      writeState("promise", hydrationPromise);
      return hydrationPromise;
    }

    function recordPrimaryRequestPatch(facetIds) {
      const pendingState = getPendingState();
      const normalizedFacetIds = Array.from(
        new Set(
          (Array.isArray(facetIds) ? facetIds : [])
            .map(function (facetId) {
              return String(facetId || "").trim();
            })
            .filter(Boolean),
        ),
      );
      if (pendingState) {
        /*
         * Proof belongs to one concrete primary request. Never merge it with
         * an older Search attempt: that request may have been aborted before a
         * later ProductListing became authoritative for hydration.
         */
        pendingState.primaryRequestFacetIds = normalizedFacetIds;
      }
      writeState("primaryRequestPatched", normalizedFacetIds.length > 0);
    }

    function wasPrimaryRequestPatched() {
      return readState("primaryRequestPatched") === true;
    }

    function wasFacetAppliedToPrimaryRequest(facetId) {
      if (!wasPrimaryRequestPatched()) return false;
      const pendingState = getPendingState();
      const appliedFacetIds = Array.isArray(
        pendingState?.primaryRequestFacetIds,
      )
        ? pendingState.primaryRequestFacetIds
        : [];
      const targetKeys = new Set(
        getCoveoUrlStateLookupKeys(facetId)
          .map(normalizeCoveoUrlFacetId)
          .filter(Boolean),
      );
      if (!targetKeys.size) return false;
      return appliedFacetIds.some(function (appliedFacetId) {
        return getCoveoUrlStateLookupKeys(appliedFacetId)
          .map(normalizeCoveoUrlFacetId)
          .some(function (lookupKey) {
            return targetKeys.has(lookupKey);
          });
      });
    }

    function wasDefaultConditionAppliedToInitialRequest() {
      return readState("defaultConditionRequestPatched") === true;
    }

    return Object.freeze({
      beginHydrationLease,
      buildDesiredFacetSelectionSnapshot,
      facetRangesEqual,
      facetValueSetsEqual,
      finishHydration,
      getFacetPriority,
      getPendingRegularFacetEntries,
      getPendingState,
      preparePendingInitialState,
      primePendingState,
      recordPrimaryRequestPatch,
      resolvePendingFacetRange,
      resolvePendingFacetValues,
      retirePendingHydration,
      scheduleHydration,
      waitForRetiredHydration,
      wasDefaultConditionAppliedToInitialRequest,
      wasFacetAppliedToPrimaryRequest,
      wasPrimaryRequestPatched,
    });
  }

  Object.defineProperty(window, "MarineMaxCoveoFilterHydration", {
    configurable: false,
    enumerable: false,
    value: Object.freeze({
      create: createCoveoFilterHydration,
    }),
    writable: false,
  });
})();
